/**
 * The recruiter's home screen.
 *
 * This is the answer to the spreadsheet's biggest failing: 456 "call back" and
 * 141 "hold" promises that nothing ever resurfaced. Recall work leads, because
 * a followup nobody sees is the same as no followup at all.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  CheckCircle2,
  Clock,
  Loader2,
  Phone,
  PhoneCall,
  Send,
} from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';
import CallsTable from '../components/CallsTable';
import { EditCallDialog, FollowUpDialog } from '../components/CallDialogs';
import { OUTCOMES } from '../lib/callOutcomes';
import { formatPhone, telHref } from '../lib/phone';
import ShareLinks from '../components/ShareLinks';
import ShiftBar from '../components/ShiftBar';
import type { Call, CallOutcome, Followup, MyDay, Page } from '../types';

export default function MyDayPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<MyDay>({
    queryKey: ['my-day'],
    queryFn: async () => (await client.get('/api/v1/my-day')).data,
  });

  const complete = useMutation({
    mutationFn: async ({ id, note }: { id: string; note?: string }) =>
      (
        await client.post(`/api/v1/followups/${id}/complete`, {
          outcomeNote: note ?? null,
        })
      ).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-day'] });
      toast.success('Followup closed');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  /**
   * Recall, as the rows themselves rather than the promises about them.
   *
   * The endpoint the Daily Calls Report reads, narrowed by recall_only to the
   * calls this person's overdue chases point at. Same rows, same table, same
   * edit -- which is the whole reason this is a call query and not the
   * followup list that used to render here as cards.
   *
   * Keyed under ['calls'] deliberately: every call mutation in the
   * application already invalidates that prefix, so this list refreshes with
   * the report instead of each of them having to learn about the dashboard.
   */
  const recall = useQuery<Page<Call>>({
    queryKey: ['calls', 'recall'],
    queryFn: async () =>
      (
        await client.get('/api/v1/calls', {
          params: { recallOnly: true, mineOnly: false, limit: 200 },
        })
      ).data,
  });

  const [editing, setEditing] = useState<Call | null>(null);
  const [editFailure, setEditFailure] = useState<string | null>(null);
  const [followingUp, setFollowingUp] = useState<Call | null>(null);

  const edit = useMutation({
    mutationFn: async ({ call, changes }: { call: Call; changes: Record<string, unknown> }) =>
      (await client.patch(`/api/v1/calls/${call.id}`, changes)).data as Call,
    onSuccess: () => {
      // The four the report invalidates, plus this page's own counts. A
      // correction can move or close the chase, so the Recall list it was
      // made from is as likely to have changed as the row inside it.
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      void queryClient.invalidateQueries({ queryKey: ['still-to-call'] });
      void queryClient.invalidateQueries({ queryKey: ['alerts'] });
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setEditing(null);
      setEditFailure(null);
      toast.success('Call corrected');
    },
    onError: (e) => setEditFailure(errorMessage(e, 'Could not correct that call')),
  });

  const followUp = useMutation({
    // The same shape the report sends: everything but the outcome and the
    // remarks comes off the call being followed up, so the new row cannot
    // disagree with the one it came from.
    mutationFn: async ({
      call,
      outcome,
      remarks,
    }: {
      call: Call;
      outcome: CallOutcome;
      remarks: string;
    }) =>
      (
        await client.post('/api/v1/calls', {
          candidateId: call.candidateId,
          clientId: call.clientId,
          position: call.position,
          outcome,
          remarks: remarks.trim() || null,
        })
      ).data as Call,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['calls'] });
      void queryClient.invalidateQueries({ queryKey: ['candidates'] });
      // Ringing back is how a recall leaves this list: the new call resolves
      // the chase behind it, and may open another.
      void queryClient.invalidateQueries({ queryKey: ['my-day'] });
      setFollowingUp(null);
      toast.success('Follow-up call logged');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="space-y-6">
      <ShiftBar />

      {/* Between the shift and the counts: the shortcuts are a start-of-day
          thing, and they are not a figure about today's work. */}
      <ShareLinks />

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat
          label="Recall"
          value={data.overdueCount}
          icon={AlertCircle}
          tone={data.overdueCount > 0 ? 'danger' : 'neutral'}
        />
        <Stat label="Due today" value={data.dueTodayCount} icon={CalendarClock} />
        <Stat label="Calls today" value={data.callsToday} icon={PhoneCall} />
        <Stat label="Submissions today" value={data.submissionsToday} icon={Send} />
      </div>

      {/* The cards this replaces were built on followups. These are the calls
          those followups point at -- the same records the Daily Calls Report
          shows, under the heading that says why they are here. */}
      <section className="space-y-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Recall</h2>
          <p className="text-xs text-gray-500">
            These were promised and have slipped past their date. Correcting a
            row here is the same edit as correcting it on the Daily Calls
            Report — one record, read in two places.
          </p>
        </div>
        {recall.isLoading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <CallsTable
            rows={recall.data?.items ?? []}
            /* Due rather than Date: the call already happened, and what makes
               the row urgent is how long the promise about it has been out. */
            dateColumn="due"
            showPosition={false}
            busy={followUp.isPending}
            emptyMessage="Nothing overdue. Every promise is still in date."
            onFollowUp={setFollowingUp}
            onEdit={(row) => {
              setEditFailure(null);
              setEditing(row);
            }}
          />
        )}
      </section>

      <FollowupSection
        title="Due Today"
        items={data.dueToday}
        onComplete={(id, note) => complete.mutate({ id, note })}
        pending={complete.isPending}
      />

      <FollowupSection
        title="Coming Up"
        items={data.upcoming}
        collapsedByDefault
        onComplete={(id, note) => complete.mutate({ id, note })}
        pending={complete.isPending}
      />

      {editing && (
        <EditCallDialog
          call={editing}
          outcomes={OUTCOMES}
          saving={edit.isPending}
          failure={editFailure}
          /* No take-over from here. It moves work between people, which is a
             decision for the report where the recruiter column is visible;
             every row on this list is the reader's own already. */
          taking={false}
          onTake={() => {}}
          onCancel={() => {
            setEditing(null);
            setEditFailure(null);
          }}
          onSave={(changes) => edit.mutate({ call: editing, changes })}
        />
      )}

      {followingUp && (
        <FollowUpDialog
          // Keyed by call, so the outcome belongs to the row it was opened on
          // rather than carrying over from the last one.
          key={followingUp.id}
          call={followingUp}
          outcomes={OUTCOMES}
          submitting={followUp.isPending}
          onCancel={() => setFollowingUp(null)}
          onConfirm={(outcome, remarks) =>
            followUp.mutate({ call: followingUp, outcome, remarks })
          }
        />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  tone = 'neutral',
}: {
  label: string;
  value: number;
  icon: typeof Clock;
  tone?: 'neutral' | 'danger';
}) {
  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">{label}</p>
        <Icon
          className={`h-4 w-4 ${tone === 'danger' ? 'text-red-500' : 'text-gray-400'}`}
        />
      </div>
      <p
        className={`mt-2 text-2xl font-semibold ${
          tone === 'danger' && value > 0 ? 'text-red-600' : 'text-gray-900'
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function FollowupSection({
  title,
  description,
  items,
  tone = 'neutral',
  collapsedByDefault = false,
  onComplete,
  pending,
}: {
  title: string;
  description?: string;
  items: Followup[];
  tone?: 'neutral' | 'danger';
  collapsedByDefault?: boolean;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [expanded, setExpanded] = useState(!collapsedByDefault);

  if (items.length === 0) {
    return (
      <section>
        <h2 className="mb-2 text-sm font-semibold text-gray-900">{title}</h2>
        <div className="card flex items-center gap-2 text-sm text-gray-500">
          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
          Nothing here.
        </div>
      </section>
    );
  }

  return (
    <section>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="mb-2 flex w-full items-center justify-between text-left"
      >
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {title}{' '}
            <span
              className={`badge ml-1 ${
                tone === 'danger'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {items.length}
            </span>
          </h2>
          {description && <p className="mt-0.5 text-xs text-gray-500">{description}</p>}
        </div>
        <span className="text-xs text-gray-400">{expanded ? 'Hide' : 'Show'}</span>
      </button>

      {expanded && (
        <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl bg-white ring-1 ring-gray-200">
          {items.map((item) => (
            <FollowupRow
              key={item.id}
              item={item}
              onComplete={onComplete}
              pending={pending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FollowupRow({
  item,
  onComplete,
  pending,
}: {
  item: Followup;
  onComplete: (id: string, note?: string) => void;
  pending: boolean;
}) {
  const [note, setNote] = useState('');
  const [open, setOpen] = useState(false);

  return (
    <li className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-gray-900">
              {item.candidateName ?? 'Unknown candidate'}
            </p>
            {item.candidatePhone && (
              <a
                href={`tel:${telHref(item.candidatePhone)}`}
                className="inline-flex items-center gap-1 whitespace-nowrap text-sm text-indigo-600 hover:underline"
              >
                <Phone className="h-3.5 w-3.5" />
                {formatPhone(item.candidatePhone)}
              </a>
            )}
            {item.isOverdue && (
              <span className="badge bg-red-100 text-red-700">Recall</span>
            )}
            {item.autoCreated && (
              <span className="badge bg-gray-100 text-gray-600" title="Created automatically from a call outcome">
                auto
              </span>
            )}
          </div>

          {item.note && (
            <p className="mt-1 line-clamp-2 text-sm text-gray-600">{item.note}</p>
          )}
          <p className="mt-1 text-xs text-gray-400">
            Due {new Date(item.dueAt).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Cancel' : 'Close'}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-3 flex gap-2">
          <input
            spellCheck={true}
            className="input"
            placeholder="What happened? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={2000}
          />
          <button
            type="button"
            className="btn-primary shrink-0"
            disabled={pending}
            onClick={() => {
              onComplete(item.id, note.trim() || undefined);
              setOpen(false);
              setNote('');
            }}
          >
            {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Done'}
          </button>
        </div>
      )}
    </li>
  );
}
